<script lang="ts">
export default {
  name: 'AnalyticsView',
};
</script>

<script setup lang="ts">
import { ref, watchEffect, onUnmounted, reactive } from 'vue';
import { tableauService } from '@/services/api';
import { TableauViz } from '@/vendor/tableau/tableau.embedding.3.19.0';
import { Maximize2 } from 'lucide-vue-next';

const props = defineProps<{
  dashboardId: string;
}>();

// -----
// refs
// -----
const tableauViz = ref<HTMLElement | null>(null);
const viz = ref<typeof TableauViz | null>(null);
const fullscreenState = reactive({
  originalWidth: 0,
  originalHeight: 0,
});
const resizing = ref(false);
let resizeTimeout: number | null = null;
const isInitialLoad = ref(true);

// --------
// watches
// --------
watchEffect(async () => {
  if (tableauViz.value) {
    console.log('Tableau viz mounted');
    await loadDashboard();
  }
});

// ----------
// functions
// ----------
async function loadDashboard() {
  if (props.dashboardId === '') {
    console.error('Dashboard ID is required.');
    return;
  }

  viz.value = new TableauViz();
  viz.value.src = await tableauService.getDashboardTrustedAuthUrlById(props.dashboardId);

  if (tableauViz.value) {
    tableauViz.value.appendChild(viz.value);

    // Add fullscreen change listener
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    new ResizeObserver(() => {
      if (isInitialLoad.value) {
        isInitialLoad.value = false;
        return;
      }

      resizing.value = true;
      console.log('Resizing Tableau viz');
      const width = tableauViz.value?.clientWidth || 0;
      // get the viewport height minus the vuetify app bar height
      const height =
        window.innerHeight -
        parseInt(getComputedStyle(document.getElementById('main')!).getPropertyValue('--v-layout-top').trim());

      viz.value?.setFrameSize(width, height);

      // Clear any existing timeout
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }

      // Set new timeout
      resizeTimeout = window.setTimeout(() => {
        resizing.value = false;
      }, 2000);
    }).observe(tableauViz.value!);
  }
}

function handleFullscreenChange() {
  if (!document.fullscreenElement && viz.value) {
    // Exited fullscreen, trigger resize
    viz.value.setFrameSize(fullscreenState.originalWidth, fullscreenState.originalHeight);
  }
}

// Clean up event listener and timeout when component is unmounted
onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  if (resizeTimeout) {
    window.clearTimeout(resizeTimeout);
  }
});

async function enterFullscreen() {
  if (!tableauViz.value) return;

  try {
    fullscreenState.originalWidth = tableauViz.value.clientWidth;
    fullscreenState.originalHeight = tableauViz.value.clientHeight;

    await tableauViz.value.requestFullscreen();
  } catch (error) {
    console.error('Error entering fullscreen:', error);
  }
}
</script>

<template>
  <div class="tw:relative tw:h-full tw:w-full">
    <div ref="tableauViz" class="tw:absolute tw:left-0 tw:top-0 tw:h-full tw:w-full"></div>
    <div
      v-if="resizing"
      class="tw:absolute tw:inset-0 tw:z-40 tw:flex tw:items-center tw:justify-center tw:bg-white/40">
      <div class="tw:loading tw:loading-spinner tw:loading-lg tw:text-primary"></div>
    </div>
    <button
      @click="enterFullscreen"
      class="tw:fixed tw:bottom-4 tw:right-4 tw:z-50 tw:rounded-full tw:bg-white tw:p-2 tw:shadow-lg tw:transition-colors tw:hover:bg-gray-100"
      title="Enter fullscreen">
      <Maximize2 class="tw:h-6 tw:w-6" />
    </button>
  </div>
</template>
